import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Users, 
  Search,
  UserPlus,
  UserMinus,
  Home,
  MapPin,
  Star,
  MessageCircle,
  MoreVertical,
  Grid3X3,
  List,
  X,
  Phone,
  Mail,
  Flag,
  Ban,
  Eye,
  Share2,
  Loader
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { generateWhatsAppLink, copyToClipboard } from '../utils/helpers';
import networkService from '../services/networkService';
import { locationService } from '../services';
import PermissionGate from '../components/PermissionGate';
import ErrorState from '../components/ErrorState';
import ConfirmDialog from '../components/ConfirmDialog/ConfirmDialog';
import DropdownMenu from '../components/DropdownMenu';
import { useToast } from '../context/ToastContext';
import './FollowingPage.css';

const FollowingPage = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'ar';
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [filterCity, setFilterCity] = useState('all');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showOffersModal, setShowOffersModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [showUnfollowDialog, setShowUnfollowDialog] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [userToAction, setUserToAction] = useState(null);

  // API state
  const [following, setFollowing] = useState([]);
  const [, setFollowers] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch following
  const fetchFollowing = useCallback(async () => {
    setLoading(true);
    try {
      const response = await networkService.getFollowing();
      if (response.success) {
        // Backend returns users array in response.data.users
        setFollowing(response.data?.users || response.data?.following || []);
      } else {
        setError(response.message || 'Failed to fetch following');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch following');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch followers
  const fetchFollowers = useCallback(async () => {
    try {
      const response = await networkService.getFollowers();
      if (response.success) {
        // Backend returns users array in response.data.users
        setFollowers(response.data?.users || response.data?.followers || []);
      }
    } catch (err) {
      console.error('Failed to fetch followers:', err);
    }
  }, []);

  // Fetch cities from API
  const fetchCities = useCallback(async () => {
    try {
      setLoadingLocations(true);
      const response = await locationService.getAllCities(currentLang);
      if (response.success && response.data.cities) {
        const citiesData = response.data.cities;
        setCities(citiesData);
      }
    } catch (err) {
      console.error('Error fetching cities:', err);
    } finally {
      setLoadingLocations(false);
    }
  }, [currentLang]);

  useEffect(() => {
    fetchCities();
    fetchFollowing();
    fetchFollowers();
  }, [fetchCities, fetchFollowing, fetchFollowers]);

  const filteredFollowing = useMemo(() => {
    if (!Array.isArray(following)) return [];
    return following.filter(user => {
      const matchesSearch = (user.name || '').includes(searchQuery) || (user.bio || '').includes(searchQuery);
      const matchesCity = filterCity === 'all' || user.city === filterCity || user.city === cities.find(c => c.label === filterCity)?.label;
      return matchesSearch && matchesCity;
    });
  }, [following, searchQuery, filterCity, cities]);

  const handleOpenUnfollowDialog = (user) => {
    setUserToAction(user);
    setShowUnfollowDialog(true);
  };

  const handleConfirmUnfollow = async () => {
    if (!userToAction) return;
    
    try {
      const response = await networkService.unfollow(userToAction._id || userToAction.id);
      if (response.success) {
        fetchFollowing();
        toast.success(t('following.unfollowed'));
      } else {
        toast.error(response.message || t('following.unfollowFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('following.unfollowFailed'));
    } finally {
      setShowUnfollowDialog(false);
      setUserToAction(null);
    }
  };

  const handleFollow = async (user) => {
    try {
      const response = await networkService.follow(user._id || user.id);
      if (response.success) {
        fetchFollowing();
        setSuggestions(prev => prev.filter(u => (u._id || u.id) !== (user._id || user.id)));
        toast.success(t('following.followed', { name: user.name }));
      } else {
        toast.error(response.message || t('following.followFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('following.followFailed'));
    }
  };

  const handleOpenMessage = (user) => {
    setSelectedUser(user);
    setMessageText('');
    setShowMessageModal(true);
    setShowMoreMenu(null);
  };

  const handleSendMessage = useCallback(() => {
    if (!messageText.trim()) {
      toast.warning(t('messages.writeMessageFirst'));
      return;
    }
    
    // Send via WhatsApp
    const whatsappLink = generateWhatsAppLink(selectedUser.phone, messageText);
    window.open(whatsappLink, '_blank');
    
    setShowMessageModal(false);
    setMessageText('');
    toast.success(t('following.whatsappOpened'));
  }, [selectedUser, messageText, t, toast]);

  const handleCallUser = (user) => {
    window.open(`tel:${user.phone}`, '_self');
    setShowMoreMenu(null);
  };

  const handleEmailUser = (user) => {
    window.open(`mailto:${user.email}`, '_blank');
    setShowMoreMenu(null);
  };

  const handleViewProfile = (user) => {
    setSelectedUser(user);
    setShowProfileModal(true);
    setShowMoreMenu(null);
  };

  const handleViewOffers = (user) => {
    setSelectedUser(user);
    setShowOffersModal(true);
    setShowMoreMenu(null);
  };

  const handleShareProfile = async (user) => {
    const profileLink = `${window.location.origin}/profile/${user._id || user.id}`;
    await copyToClipboard(profileLink);
    toast.success(t('following.profileLinkCopied'));
    setShowMoreMenu(null);
  };

  const handleReportUser = (user) => {
    toast.success(t('following.reported', { name: user.name }));
    setShowMoreMenu(null);
  };

  const handleOpenBlockDialog = (user) => {
    setUserToAction(user);
    setShowBlockDialog(true);
    setShowMoreMenu(null);
  };

  const handleConfirmBlock = async () => {
    if (!userToAction) return;
    
    try {
      // First unfollow, then block
      await networkService.unfollow(userToAction._id || userToAction.id);
      fetchFollowing();
      toast.success(t('following.blocked', { name: userToAction.name }));
    } catch (err) {
      toast.error(err.message || t('following.blockFailed'));
    } finally {
      setShowBlockDialog(false);
      setUserToAction(null);
    }
  };

  const toggleMoreMenu = (userId) => {
    setShowMoreMenu(showMoreMenu === userId ? null : userId);
  };

  const handleOfferClick = (offer) => {
    toast.info(`عرض: ${offer.title} - ${(offer.price || 0).toLocaleString()} ر.س`);
  };

  if (loading) {
    return (
      <div className="following-page">
        <div className="loading-container">
          <Loader className="spinner" size={48} />
          <p>{t('following.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="following-page">
        <ErrorState error={error} onRetry={fetchFollowing} />
      </div>
    );
  }

  return (
    <PermissionGate feature="PAGE_FOLLOWING" type="page">
    <div className="following-page">
      <div className="page-header">
        <div className="header-title">
          <Users size={28} />
          <h1>{t('following.title')}</h1>
          <span className="count">{following.length} {t('following.follower')}</span>
        </div>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <Search size={20} />
          <input 
            type="text"
            placeholder={t('common.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="filters">
          <div className="filter-group">
            <select value={filterCity} onChange={(e) => setFilterCity(e.target.value)} disabled={loadingLocations}>
              <option value="all">{loadingLocations ? 'جاري التحميل...' : t('following.allCities')}</option>
              {cities.map(city => (
                <option key={city._id} value={city.label}>{city.label}</option>
              ))}
            </select>
          </div>
          
          <div className="view-toggle">
            <button 
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
            >
              <Grid3X3 size={18} />
            </button>
            <button 
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              <List size={18} />
            </button>
          </div>
        </div>
      </div>

      {filteredFollowing.length === 0 ? (
        <div className="empty-state">
          <Users size={64} />
          <h3>{t('following.noFollowing')}</h3>
          <p>{t('following.startFollowing')}</p>
        </div>
      ) : (
        <div className={`users-grid ${viewMode}`}>
          {filteredFollowing.map(user => {
            const userId = user._id || user.id;
            return (
              <div key={userId} className="user-card">
                <div className="user-header">
                  <div className="user-avatar large">
                    {user.avatar ? (
                      <img src={user.avatar} alt={user.name} />
                    ) : (
                      (user.name || 'U').charAt(0)
                    )}
                  </div>
                  <div className="user-info">
                    <h3>{user.name}</h3>
                    <span className="user-city">
                      <MapPin size={14} />
                      {user.city || t('following.notSpecified')}
                    </span>
                  </div>
                  <DropdownMenu
                    isOpen={showMoreMenu === userId}
                    onClose={() => setShowMoreMenu(null)}
                    trigger={
                      <button className="more-btn" onClick={() => toggleMoreMenu(userId)}>
                        <MoreVertical size={18} />
                      </button>
                    }
                  >
                    <button onClick={() => handleViewProfile(user)}>
                      <Eye size={16} />
                      {t('following.viewProfile')}
                    </button>
                    <button onClick={() => handleViewOffers(user)}>
                      <Home size={16} />
                      {t('following.viewOffers')} ({user.offersCount || 0})
                    </button>
                    <button onClick={() => handleCallUser(user)}>
                      <Phone size={16} />
                      {t('following.call')}
                    </button>
                    <button onClick={() => handleEmailUser(user)}>
                      <Mail size={16} />
                      {t('following.email')}
                    </button>
                    <button onClick={() => handleShareProfile(user)}>
                      <Share2 size={16} />
                      {t('following.shareProfile')}
                    </button>
                    <div className="dropdown-divider"></div>
                    <button className="delete-option" onClick={() => handleReportUser(user)}>
                      <Flag size={16} />
                      {t('following.report')}
                    </button>
                    <button className="delete-option" onClick={() => handleOpenBlockDialog(user)}>
                      <Ban size={16} />
                      {t('following.block')}
                    </button>
                  </DropdownMenu>
              </div>

              <p className="user-bio">{user.bio}</p>

              <div className="user-stats">
                <div className="stat">
                  <Home size={16} />
                  <span>{user.offersCount} {t('following.offers')}</span>
                </div>
                <div className="stat">
                  <Users size={16} />
                  <span>{user.followers} {t('following.follower')}</span>
                </div>
                <div className="stat">
                  <Star size={16} />
                  <span>{user.rating}</span>
                </div>
              </div>

              {user.recentOffers && user.recentOffers.length > 0 && (
                <div className="recent-offers">
                  <span className="label">{t('following.recentOffers')}:</span>
                  <div className="offers-tags">
                    {user.recentOffers.slice(0, 2).map((offer, idx) => (
                      <span 
                        key={idx} 
                        className="offer-tag"
                        onClick={() => handleOfferClick(offer)}
                      >
                        {offer.title}
                      </span>
                    ))}
                    {(user.recentOffers || []).length > 2 && (
                      <span 
                        className="offer-tag more"
                        onClick={() => handleViewOffers(user)}
                      >
                        +{(user.recentOffers || []).length - 2}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="user-actions">
                <button className="btn btn-outline" onClick={() => handleOpenMessage(user)}>
                  <MessageCircle size={18} />
                  {t('following.message')}
                </button>
                <button 
                  className="btn btn-unfollow"
                  onClick={() => handleOpenUnfollowDialog(user)}
                >
                  <UserMinus size={18} />
                  {t('following.unfollow')}
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="suggestions-section">
          <h2>{t('following.suggestions')}</h2>
          <div className="suggestions-grid">
            {suggestions.map(user => (
              <div key={user._id || user.id} className="suggestion-card">
                <div className="user-avatar">
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.name} />
                  ) : (
                    (user.name || 'U').charAt(0)
                  )}
                </div>
                <div className="suggestion-info">
                  <h4>{user.name}</h4>
                  <span className="city">{user.city || t('following.notSpecified')}</span>
                  <span className="stats">{user.offersCount || 0} {t('following.offers')} • {user.followers || 0} {t('following.follower')}</span>
                </div>
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={() => handleFollow(user)}
                >
                  <UserPlus size={16} />
                  {t('following.follow')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Message Modal */}
      {showMessageModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowMessageModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <MessageCircle size={24} />
                {t('following.messageUser', { name: selectedUser.name })}
              </h2>
              <button className="modal-close" onClick={() => setShowMessageModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="message-recipient">
                <div className="user-avatar">
                  {selectedUser.avatar ? (
                    <img src={selectedUser.avatar} alt={selectedUser.name} />
                  ) : (
                    (selectedUser.name || 'U').charAt(0)
                  )}
                </div>
                <div>
                  <strong>{selectedUser.name}</strong>
                  <span>{selectedUser.city}</span>
                </div>
              </div>
              
              <div className="form-group">
                <label>{t('following.messageLabel')}</label>
                <textarea 
                  className="form-control"
                  rows={4}
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder={t('following.messagePlaceholder')}
                />
              </div>

              <div className="quick-messages">
                <span className="label">{t('following.quickMessages')}:</span>
                <div className="quick-btns">
                  <button onClick={() => setMessageText('السلام عليكم، أريد الاستفسار عن عروضك العقارية')}>
                    {t('following.quickInquiry')}
                  </button>
                  <button onClick={() => setMessageText('السلام عليكم، هل يمكنك مساعدتي في البحث عن عقار؟')}>
                    {t('following.quickHelp')}
                  </button>
                  <button onClick={() => setMessageText('السلام عليكم، أريد التعاون معك في التسويق العقاري')}>
                    {t('following.quickCooperate')}
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowMessageModal(false)}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary" onClick={handleSendMessage}>
                <FaWhatsapp size={18} />
                {t('following.sendViaWhatsapp')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {showProfileModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowProfileModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('following.profile')}</h2>
              <button className="modal-close" onClick={() => setShowProfileModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="profile-header">
                <div className="user-avatar large">
                  {selectedUser.avatar ? (
                    <img src={selectedUser.avatar} alt={selectedUser.name} />
                  ) : (
                    (selectedUser.name || 'U').charAt(0)
                  )}
                </div>
                <div className="profile-info">
                  <h3>{selectedUser.name}</h3>
                  <span className="city"><MapPin size={14} /> {selectedUser.city}</span>
                  <div className="rating">
                    <Star size={16} fill="#ffc107" color="#ffc107" />
                    <span>{selectedUser.rating}</span>
                  </div>
                </div>
              </div>

              <p className="bio">{selectedUser.bio}</p>

              <div className="profile-stats">
                <div className="stat">
                  <span className="value">{selectedUser.offersCount}</span>
                  <span className="label">{t('following.offers')}</span>
                </div>
                <div className="stat">
                  <span className="value">{selectedUser.followers}</span>
                  <span className="label">{t('following.follower')}</span>
                </div>
                <div className="stat">
                  <span className="value">{selectedUser.rating}</span>
                  <span className="label">{t('following.rating')}</span>
                </div>
              </div>

              <div className="contact-info">
                <h4>{t('following.contactInfo')}</h4>
                <div className="contact-item" onClick={() => handleCallUser(selectedUser)}>
                  <Phone size={18} />
                  <span>{selectedUser.phone}</span>
                </div>
                <div className="contact-item" onClick={() => handleEmailUser(selectedUser)}>
                  <Mail size={18} />
                  <span>{selectedUser.email}</span>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowProfileModal(false)}>
                {t('common.close')}
              </button>
              <button className="btn btn-primary" onClick={() => {
                setShowProfileModal(false);
                handleOpenMessage(selectedUser);
              }}>
                <MessageCircle size={18} />
                {t('following.message')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offers Modal */}
      {showOffersModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowOffersModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Home size={24} />
                {t('following.offersTitle', { name: selectedUser.name })}
              </h2>
              <button className="modal-close" onClick={() => setShowOffersModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              {selectedUser.recentOffers && selectedUser.recentOffers.length > 0 ? (
                <div className="offers-list">
                  {selectedUser.recentOffers.map(offer => (
                    <div key={offer.id} className="offer-item">
                      <div className="offer-icon">
                        <Home size={24} />
                      </div>
                      <div className="offer-details">
                        <h4>{offer.title}</h4>
                        <span className="location"><MapPin size={14} /> {offer.location}</span>
                      </div>
                      <div className="offer-price">
                        {offer.price.toLocaleString()} {t('common.sar')}
                      </div>
                      <button className="btn btn-outline btn-sm" onClick={() => handleOfferClick(offer)}>
                        <Eye size={16} />
                        {t('common.view')}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-offers">
                  <Home size={48} />
                  <p>{t('following.noOffers')}</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowOffersModal(false)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Unfollow Dialog */}
      <ConfirmDialog
        isOpen={showUnfollowDialog}
        onClose={() => { setShowUnfollowDialog(false); setUserToAction(null); }}
        onConfirm={handleConfirmUnfollow}
        title={t('following.confirmUnfollowTitle') || 'إلغاء المتابعة'}
        message={t('following.confirmUnfollowMessage', { name: userToAction?.name }) || `هل أنت متأكد من إلغاء متابعة ${userToAction?.name}؟`}
        confirmText={t('following.unfollow') || 'إلغاء المتابعة'}
        cancelText={t('common.cancel') || 'إلغاء'}
        type="warning"
      />

      {/* Confirm Block Dialog */}
      <ConfirmDialog
        isOpen={showBlockDialog}
        onClose={() => { setShowBlockDialog(false); setUserToAction(null); }}
        onConfirm={handleConfirmBlock}
        title={t('following.confirmBlockTitle') || 'حظر المستخدم'}
        message={t('following.confirmBlockMessage', { name: userToAction?.name }) || `هل أنت متأكد من حظر ${userToAction?.name}؟ لن تتمكن من رؤية عروضه.`}
        confirmText={t('following.block') || 'حظر'}
        cancelText={t('common.cancel') || 'إلغاء'}
        type="danger"
      />
    </div>
    </PermissionGate>
  );
};

export default FollowingPage;
